import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { getGuidScreenByRoute, getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import AccordionQuoteHistory from "@/main/components/shared/accordionQuoteHistory/accordionQuoteHistory";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import { RoutesConstants } from "@/main/routes/routesConstants";
import { Controls } from "@/main/services/constants/constantsControl";
import { userIsAdmin, userIsBodyshop } from "@/main/services/utilsUser";

import useKogControlElementId from "@/main/hooks/useKogControlElementId";
import { useControlReducer } from "@/main/store/control";

import { usePreQuoteContext } from "../context";
import WrapperModalDontAccessPreQuote from "../modalDontAccessPreQuote/modalDontAccessPreQuote";
import { checkPendingGlass, checkPendingTire } from "../utilsPrequote";
import * as SPage from "../styles";
import * as S from "./styles";
import ColumnLabel from "@/main/components/common/columnLabel/columnLabel";

interface ISectionIssuesProps {
	setOpenModalImages: React.Dispatch<React.SetStateAction<boolean>>;
}

function SectionIssues({ setOpenModalImages }: ISectionIssuesProps) {
	const [searchParams] = useSearchParams();
	const guidQuote = searchParams.get("guidQuote");

	const showPrices = useKogControlElementId(Controls.IdShowPrices);
	const CONTROL_VEHICLE_SECTION = useKogControlElementId(Controls.IdVehicleSession);
	const CONTROL_GLASS_SECTION = useKogControlElementId(Controls.IdGlassSession);
	const CONTROL_TIRE_SECTION = useKogControlElementId(Controls.IdTireSession);
	const CONTROL_BODYSHOP_SECTION = useKogControlElementId(Controls.IdBodyshopSession);
	const { controlsByScreen } = useControlReducer();
	const { preQuoteData, isLoadingInfoOrder } = usePreQuoteContext();

	const [modalHistory, setModalHistory] = useState<boolean>(false);
	const [isOpenModalAlert, setIsOpenModalAlert] = useState<boolean>(false);

	const hasPendingVehicle = CONTROL_VEHICLE_SECTION && preQuoteData?.vehiclePending;
	const hasPendingGlass = CONTROL_GLASS_SECTION && checkPendingGlass(preQuoteData);
	const hasPendingTire = CONTROL_TIRE_SECTION && checkPendingTire(preQuoteData);
	const hasPendingBodyshop =
		CONTROL_BODYSHOP_SECTION &&
		!isNullUndefined(preQuoteData) &&
		preQuoteData.bodyshopPending &&
		(userIsAdmin() || userIsBodyshop());

	const MAP_CONTROL_PENDING = {
		vehicle: {
			sectionPending: preQuoteData?.vehiclePending,
			hasControl: CONTROL_VEHICLE_SECTION,
		},
		glass: {
			sectionPending: checkPendingGlass(preQuoteData),
			hasControl: CONTROL_GLASS_SECTION,
		},
		tire: {
			sectionPending: checkPendingTire(preQuoteData),
			hasControl: CONTROL_TIRE_SECTION,
		},
		bodyshop: {
			sectionPending: preQuoteData?.bodyshopPending,
			hasControl: CONTROL_BODYSHOP_SECTION,
		},
	};

	const renderVehicle = () => {
		if (isNullUndefined(preQuoteData)) return "";
		if (preQuoteData.vehiclePending) return preQuoteData.vehicleIntegration;

		return `${preQuoteData.vehicleManufacturer} ${preQuoteData.vehicleModel} ${preQuoteData.vehicleSubModel} ${preQuoteData.vehicleYear}`;
	};

	useEffect(() => {
		if (
			controlsByScreen.isLoading ||
			isNullUndefined(controlsByScreen.data) ||
			isLoadingInfoOrder ||
			isNullUndefined(preQuoteData)
		)
			return;

		const listPending = Object.values(MAP_CONTROL_PENDING).filter(
			(control) => control.sectionPending && !control.hasControl,
		);
		const listHasAccess = Object.values(MAP_CONTROL_PENDING).filter(
			(control) => control.sectionPending && control.hasControl,
		);

		if (listPending.length > 0 && listHasAccess.length === 0) {
			setIsOpenModalAlert(true);
		}
	}, [JSON.stringify(preQuoteData), JSON.stringify(controlsByScreen)]);

	return (
		<>
			<SPage.ContainerBox>
				<SPage.ColumnLabelButton>
					<S.AreaInfoOrder>
						<ColumnLabel
							label={getTranslateByKey("information.vehicle")}
							content={renderVehicle()}
							propsLabel={{ style: { fontSize: "1rem" } }}
						/>
						<ColumnLabel
							label={getTranslateByKey("information.claimNumber")}
							content={preQuoteData?.claimNumber}
							propsLabel={{ style: { fontSize: "1rem" } }}
						/>
						<ColumnLabel
							label={getTranslateByKey("information.insurer")}
							content={preQuoteData?.insurer}
							propsLabel={{ style: { fontSize: "1rem" } }}
						/>
					</S.AreaInfoOrder>
					<S.AreaButtons>
						<S.ButtonHistory>
							<SecondaryButton id="id-preQuote-button-history" type="button" onClick={() => setModalHistory(true)}>
								{getTranslateByKey("information.history")}
							</SecondaryButton>
						</S.ButtonHistory>
						<SPage.ButtonImages>
							<SecondaryButton id="id-preQuote-button-images" type="button" onClick={() => setOpenModalImages(true)}>
								{`${getTranslateByKey("information.images")} / ${getTranslateByKey("information.files")}`}
							</SecondaryButton>
						</SPage.ButtonImages>
					</S.AreaButtons>
				</SPage.ColumnLabelButton>
				<SPage.ColumnLabelButton>
					<S.AreaIssues>
						<S.LabelTitle>{getTranslateByKey("information.pendingIssues")}</S.LabelTitle>
						{hasPendingVehicle && (
							<S.LabelText>{`*${getTranslateByKey("information.fixVehicleInformation")}`}</S.LabelText>
						)}
						{hasPendingBodyshop && (
							<S.LabelText>{`*${getTranslateByKey("information.fixBodyshopInformation")}`}</S.LabelText>
						)}
						{hasPendingGlass && <S.LabelText>{`*${getTranslateByKey("information.fixGlassInformation")}`}</S.LabelText>}
						{hasPendingTire && <S.LabelText>{`*${getTranslateByKey("information.fixTireInformation")}`}</S.LabelText>}
					</S.AreaIssues>
				</SPage.ColumnLabelButton>
				<KogModalSideComponent
					open={modalHistory}
					closeModal={() => setModalHistory(false)}
					title={getTranslateByKey("information.history")}
				>
					{modalHistory && (
						<>
							<S.ContentAccordionQuoteHistory>
								<AccordionQuoteHistory
									guidScreen={getGuidScreenByRoute(RoutesConstants.orderCreateOrder)}
									showPrices={showPrices}
									guidQuote={guidQuote}
								/>
							</S.ContentAccordionQuoteHistory>
							<S.FooterButtonsHistory>
								<SecondaryButton onClick={() => setModalHistory(false)} id="id-preQuote-button-back">
									{getTranslateByKey("information.back")}
								</SecondaryButton>
							</S.FooterButtonsHistory>
						</>
					)}
				</KogModalSideComponent>

				<WrapperModalDontAccessPreQuote
					isOpenModal={isOpenModalAlert}
					setIsOpenModal={setIsOpenModalAlert}
					closeModal={() => setIsOpenModalAlert(false)}
				/>
			</SPage.ContainerBox>
		</>
	);
}

export default SectionIssues;
